import { useState, useEffect, useCallback, useRef } from 'react';

import { supabase } from '../../../lib/supabase';
import type { ChatMessage } from '../../../types/multiplayer';

export function useRoomChat(roomId: string, userId: string, channel: any) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load initial messages
  const loadMessages = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('multiplayer_game_room_chat_messages')
        .select('*')
        .eq('room_id', roomId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load messages';
      console.error('Error loading messages:', errorMessage);
      setError(errorMessage);
    }
  }, [roomId, setError]);

  // Send a new message
  const sendMessage = useCallback(async (content: string) => {
    if (!userId || !content.trim()) return;

    try {
      const { error } = await supabase
        .from('multiplayer_game_room_chat_messages')
        .insert([
          {
            room_id: roomId,
            user_id: userId,
            content: content.trim(),
          },
        ]);

      if (error) throw error;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
      console.error('Error sending message:', errorMessage);
      setError(errorMessage);
    }
  }, [roomId, userId, setError]);

  // Handle typing indicator
  const handleTyping = useCallback((isUserTyping: boolean) => {
    if (!channel) return;
    
    setIsTyping(isUserTyping);
    
    // Clear any existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Set a timeout to stop typing after 3 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
    
    // Broadcast typing status
    channel.track({
      isTyping: isUserTyping,
      userId,
      timestamp: new Date().toISOString(),
    });
  }, [userId, channel]);

  // Set up real-time subscription
  useEffect(() => {
    if (!roomId) return;

    // Subscribe to new messages
    const chatSubscription = supabase
      .channel(`room:${roomId}:chat`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
          filter: `room_id=eq.${roomId}`,
        },
        (payload) => {
          setMessages((prev) => [...prev, payload.new as ChatMessage]);
        }
      )
      .subscribe();

    // Set up typing indicator channel
    const typingChannel = supabase.channel(`room:${roomId}:typing`, {
      config: {
        presence: {
          key: userId || 'anonymous',
        },
      },
    });

    typingChannel
      .on('presence', { event: 'sync' }, () => {
        const state = typingChannel.presenceState();
        // Handle typing indicators from other users
        console.log('Presence state:', state);
      })
      .subscribe(async (status: string) => {
        if (status === 'SUBSCRIBED') {
          await typingChannel.track({
            online_at: new Date().toISOString(),
            userId,
          });
        }
      });

    // Load initial messages
    loadMessages();

    // Cleanup
    return () => {
      chatSubscription.unsubscribe();
      if (typingChannel) {
        typingChannel.unsubscribe();
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [roomId, userId, loadMessages]);

  return {
    messages,
    isTyping,
    sendMessage,
    handleTyping,
  };
}
